<template>
  <transition name="fade">
    <div v-if="isVisible" class="avatar-generator" role="dialog" aria-modal="true">
      <div class="modal-backdrop" @click="closeGenerator"></div>

      <div class="modal-card" @click.stop>
        <div class="modal-card__top">
          <h2>Выбор аватара</h2>
          <button class="modal-close" aria-label="Закрыть" @click="closeGenerator">×</button>
        </div>

        <div class="modal-body">
          <div class="avatar-wrapper" title="Клик — новая рандомная аватарка" @click="generateRandomAvatar">
            <div class="avatar-background" :style="{ background: bgGradient }" />
            <Avatar v-bind="avatarProps" class="avatar-component" />
          </div>

          <button class="btn" @click="generateRandomAvatar">
            <Icon name="forms" size="20" />
            <span>Сгенерировать</span>
          </button>

          <div class="controls">
            <button class="btn cancel" @click="closeGenerator">Отменить</button>
            <button class="btn primary" @click="selectAvatar">Выбрать</button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch, onBeforeUnmount } from 'vue';
import { Avatar, Factory } from 'vue3-avataaars';

export default {
  name: 'AvatarGenerator',
  components: { Avatar },
  props: {
    modelValue: Object,
    visible: Boolean
  },
  emits: ['update:modelValue', 'update:visible'],
  setup(props, { emit }) {
    const isVisible = ref(props.visible);
    const avatarProps = ref({ ...Factory() });
    const bgGradient = ref('');

    const COLORS = [
      ['#FFB6C1','#FFC0CB','#FFDAB9'],
      ['#B3E5FC','#81D4FA','#4FC3F7'],
      ['#E1BEE7','#CE93D8','#BA68C8'],
      ['#C8E6C9','#A5D6A7','#66BB6A'],
      ['#FFF9C4','#FFF59D','#FFE082'],
      ['#FFCCBC','#FFAB91','#FF8A65'],
      ['#D7CCC8','#BCAAA4','#A1887F']
    ];

    const SKIN_COLORS = ['Tanned','Yellow','Pale','Light','Brown','DarkBrown','Black'];

    const randChoice = arr => arr[Math.floor(Math.random() * arr.length)];
    const randomGradient = () => `radial-gradient(circle at 30% 30%, ${randChoice(COLORS).join(',')})`;

    const buildRandomAvatar = () => {
      const avatar = { ...Factory() };
      avatar.skinColor = randChoice(SKIN_COLORS);
      return avatar;
    };

    const generateRandomAvatar = () => {
      avatarProps.value = buildRandomAvatar();
      bgGradient.value = randomGradient();
    };

    const closeGenerator = () => {
      isVisible.value = false;
      emit('update:visible', false);
    };

    const selectAvatar = () => {
      emit('update:modelValue', JSON.parse(JSON.stringify(avatarProps.value)));
      closeGenerator();
    };

    // --- Scroll lock ---
    const scrollY = ref(0);
    const lockScroll = () => {
      if (document.body.classList.contains('modal-scroll-locked')) return;
      scrollY.value = window.scrollY;
      Object.assign(document.body.style, {
        position: 'fixed',
        top: `-${scrollY.value}px`,
        left: 0,
        right: 0
      });
      document.body.classList.add('modal-scroll-locked');
    };
    const unlockScroll = () => {
      if (!document.body.classList.contains('modal-scroll-locked')) return;
      document.body.classList.remove('modal-scroll-locked');
      Object.assign(document.body.style, { position: '', top: '', left: '', right: '' });
      window.scrollTo(0, scrollY.value || 0);
      scrollY.value = 0;
    };

    watch(() => props.visible, val => {
      isVisible.value = val;
      if (val) {
        generateRandomAvatar();
        lockScroll();
      } else unlockScroll();
    });

    onBeforeUnmount(() => unlockScroll());

    return { isVisible, avatarProps, bgGradient, generateRandomAvatar, selectAvatar, closeGenerator };
  }
};
</script>

<style scoped>
.avatar-generator {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(2px);
  z-index: 1;
}

.modal-card {
  position: relative;
  z-index: 2;
  width: 28rem;
  max-width: calc(100% - 2rem);
  background: var(--grey-1);
  border-radius: 1rem;
  box-shadow: 0 20px 40px rgba(2,6,23,0.35), 0 2px 6px rgba(2,6,23,0.15);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.modal-close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 3;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  border: none;
  background: transparent;
  font-size: 1.25rem;
  cursor: pointer;
  color: var(--color-0);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-close:hover { opacity: 0.5; }

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  width: 100%;
}

.avatar-wrapper {
  width: 12rem;
  height: 12rem;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 1rem;
  cursor: pointer;
}

.avatar-background {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10rem;
  height: 10rem;
  border-radius: 50%;
  z-index: 0;
  box-shadow: inset 0 6px 18px rgba(0,0,0,0.06);
}

.avatar-component {
  width: 10rem;
  height: 10rem;
  z-index: 1;
  border-radius: 50%;
}

.cancel {
  background-color: transparent;
  border: 0.1rem solid var(--color-0);
  color: var(--color-0);
}

.controls {
  margin-top: 0.25rem;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  width: 100%;
}

.btn:hover { opacity: 0.8; }

.fade-enter-active, .fade-leave-active { transition: opacity 180ms ease, transform 180ms ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: scale(0.98); }
.fade-enter-to, .fade-leave-from { opacity: 1; transform: scale(1); }
</style>
